import { useState, useCallback } from "react";
import useApi from "./useApi";
import usePage from "./usePage";

const useOrderDashboard = () => {
  const { postData } = useApi();
  const { showMessage } = usePage();

  const [filters, setFilters] = useState({
    period: "week",
    startDate: null,
    endDate: null,
    reps: [],
    locations: [],
  });

  // Metrics state
  const [metrics, setMetrics] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [orderVolume, setOrderVolume] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [ordersByRep, setOrdersByRep] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [ordersByLocation, setOrdersByLocation] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [topReps, setTopReps] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [repOrderTrends, setRepOrderTrends] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [topSkus, setTopSkus] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [skuCategories, setSkuCategories] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [trendingSkus, setTrendingSkus] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [orderComposition, setOrderComposition] = useState({
    data: null,
    isLoading: false,
    error: null,
  });

  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const fetchData = async (action, setState) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const result = await postData({
        entityName: "Order",
        action,
        data: filters,
      });

      if (result.isSuccessful) {
        setState({ data: result.data, isLoading: false, error: null });
      } else {
        setState({ data: null, isLoading: false, error: result.message });
      }
    } catch (err) {
      const errorMsg = `Error fetching ${action}: ${err.message}`;
      showMessage(errorMsg, "error");
      setState({ data: null, isLoading: false, error: err.message });
    }
  };

  const getMetrics = useCallback(
    () => fetchData("getMetrics", setMetrics),
    [filters]
  );
  const getOrderVolume = useCallback(
    () => fetchData("getOrderVolume", setOrderVolume),
    [filters]
  );
  const getOrdersByRep = useCallback(
    () => fetchData("getOrdersByRep", setOrdersByRep),
    [filters]
  );
  const getOrdersByLocation = useCallback(
    () => fetchData("getOrdersByLocation", setOrdersByLocation),
    [filters]
  );
  const getTopReps = useCallback(
    () => fetchData("getTopReps", setTopReps),
    [filters]
  );
  const getRepOrderTrends = useCallback(
    () => fetchData("getRepOrderTrends", setRepOrderTrends),
    [filters]
  );
  const getTopSkus = useCallback(
    () => fetchData("getTopSkus", setTopSkus),
    [filters]
  );
  const getSkuCategories = useCallback(
    () => fetchData("getSkuCategories", setSkuCategories),
    [filters]
  );
  const getTrendingSkus = useCallback(
    () => fetchData("getTrendingSkus", setTrendingSkus),
    [filters]
  );
  const getOrderComposition = useCallback(
    () => fetchData("getOrderComposition", setOrderComposition),
    [filters]
  );

  return {
    filters,
    updateFilters,
    metrics,
    orderVolume,
    ordersByRep,
    ordersByLocation,
    topReps,
    repOrderTrends,
    topSkus,
    skuCategories,
    trendingSkus,
    orderComposition,
    isLoading: metrics.isLoading || orderVolume.isLoading,
    getMetrics,
    getOrderVolume,
    getOrdersByRep,
    getOrdersByLocation,
    getTopReps,
    getRepOrderTrends,
    getTopSkus,
    getSkuCategories,
    getTrendingSkus,
    getOrderComposition,
  };
};

export default useOrderDashboard;
